import { BASEURL } from '@services/network';
import { createAuthedEventSource } from '@utils/streamAuth';
import { logError, logger } from '@utils/usefulFuncs';

export function streamQuerySeriesResults({ resultId, onMessage, onComplete, onError }) {
  let source = null;

  const cancel = () => {
    if (source) {
      source.close();
      source = null;
    }
  };

  if (!resultId) {
    onError?.('Missing result id');
    return { cancel };
  }

  const url = `${BASEURL}/api/v2/genai/queries/stream_events?run_id=${encodeURIComponent(resultId)}&include_context=true`;
  source = createAuthedEventSource(url);

  const handleMessage = (event) => {
    try {
      const streamData = JSON.parse(event?.data || '{}');
      const rowIndex = streamData?.question_index;
      logger('streamQuerySeriesResults onMessage', 'received', {
        runId: resultId,
        rowIndex,
        finalAnswer: streamData?.final_answer === true,
        answerLength: streamData?.answer?.length || 0,
      });
      if (rowIndex == null) return;
      onMessage?.({
        rowIndex,
        message: streamData.answer || '',
        finalAnswer: streamData.final_answer === true,
      });
    } catch (error) {
      logError('QuerySeries stream message parse error', error);
    }
  };

  const handleComplete = (event) => {
    try {
      const streamData = JSON.parse(event?.data || '{}');
      const answers = streamData?.response?.answers || [];
      const answersWithContexts = [];
      answers.forEach((row) => {
        if (!row || row.question_index == null) return;
        const existing = answersWithContexts[row.question_index];
        if (!existing || row.final_answer) {
          answersWithContexts[row.question_index] = {
            answer: row.answer || '',
            context: row.context,
            context_with_all_headers: row.context_with_all_headers,
            sources: row.sources,
          };
        }
      });
      onComplete?.({ answersWithContexts });
    } catch (error) {
      logError('QuerySeries stream complete parse error', error);
      onError?.('Could not parse completed stream payload');
    } finally {
      source?.removeEventListener('message', handleMessage);
      source?.removeEventListener('error', handleError);
      source?.removeEventListener('complete', handleComplete);
      cancel();
    }
  };

  const handleError = (error) => {
    logError('QuerySeries stream error', error);
    onError?.('Streaming failed');
    source?.removeEventListener('message', handleMessage);
    source?.removeEventListener('error', handleError);
    source?.removeEventListener('complete', handleComplete);
    cancel();
  };

  source.addEventListener('message', handleMessage);
  source.addEventListener('complete', handleComplete);
  source.addEventListener('error', handleError);

  return { cancel };
}
